import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import isString from "lodash/isString";
import type { JSX } from 'react';
import { FunctionComponent } from "react";
import { Navigate, To, generatePath, useParams } from "react-router-dom";
import { createJSONStorage, persist } from "zustand/middleware";
import { StateCreator, createStore } from "zustand/vanilla";
import { notNil } from "../core/utils";
import { displayError } from "../core/utils/modals";
import { DatosConfiguracion, obtenerConfiguracion } from "../features/public/common/api/common.api";
import useSessionStore, { createWithSelectors } from "./use-session";

class NotFound { }

type Empty = Record<string, never>;

export const isDatosConfiguracion = (value: any): value is DatosConfiguracion =>
	notNil(value) && Object.hasOwn(value, "nombreCorto");

export const obtenerNombreComercio = (value: any) =>
	isDatosConfiguracion(value) ? value.nombre : "N/A";

const sameSegment = (configuration: DatosConfiguracion | {}, segment: string) =>
	get(configuration, "nombreCorto", "").toUpperCase() === segment.toUpperCase();

export const withRouting = <T extends object>(Component: FunctionComponent<T>, redirectTo: To) =>
	(props: T): JSX.Element | null => {
		const init = useRouting.use.init();
		const signout = useSessionStore.use.signout();
		const configuration = useRouting.use.configuration();
		const { segmento: routeSegment } = useParams();

		if (isNil(routeSegment) || configuration instanceof NotFound) {
			return <Navigate to={redirectTo} replace />;
		}

		if (!sameSegment(configuration, routeSegment)) {
			!isEmpty(configuration) && signout();
			init(routeSegment).catch(displayError);
			return null;
		}

		return <Component {...props} />;
	};

type RoutingState = {
	currentSegment: string;
	configuration: DatosConfiguracion | NotFound | Empty;
	init: (segment: string) => Promise<void>;
	createPath: (path: string) => string;
};

const notFound = { configuration: new NotFound(), currentSegment: "" };

const store: StateCreator<RoutingState> = (set, get) => ({
	currentSegment: "",
	configuration: {},
	init: async (segment: string) => {
		const config = await obtenerConfiguracion(segment);
		if (config) {
			set({ configuration: config, currentSegment: config.nombreCorto });
		} else {
			set(notFound);
		}
	},
	createPath: (path: string) => {
		const segmento = get().currentSegment;
		return isString(segmento) ? generatePath(path, { segmento }) : "/404";
	}
});

const routingStore = createStore(persist(
	store,
	{
		name: "portal-config-session",
		storage: createJSONStorage(() => sessionStorage)
	}
));

export const useRouting = createWithSelectors(routingStore);

export default withRouting;